import { getIntegrationViewConfig } from '../../connections/connections.config.js';
import { rootQueryKeys } from './rootQueryKeys.js';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import React__default from 'react';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled, getNextPageParam } from '@fabric-app/queries/utilities';
import { useQueryDirectSubfolderCountPerResourceId } from '../../resources/queries/useQueryDirectChildCountPerResourceId.js';
import { isInExtensionPlatform } from '@fabric-app/compat/capabilities/platform';

const initialPageParam = {
  /**
  * @TODO
  * unreasonable, we don't handle continuous fetching in UI just yet
  * we probably need to load all at once anyway, but batch loading could be a good idea
  */
  limit: 500,
  offset: 0,
  count: 0
};
const useQueryResourceRootList = (queryFilter, queryConfig) => {
  const { client } = useWoody();
  const { data: user } = useQueryUserInfo();
  const query = useInfiniteQuery({
    queryKey: rootQueryKeys.resourceRootList(queryFilter),
    queryFn: async ({ pageParam: { limit, offset } }) => {
      const response = await client.v2("/v2/resource-roots", {
        query: {
          ...queryFilter,
          limit,
          offset,
          includeMembers: 8,
          includeResources: 3
        }
      });
      const { integrations, sharedWith, resources } = response.data;
      const roots = response.data.roots;
      const mappedRoots = roots.map((root) => {
        const rootSharedWith = sharedWith?.[root.id];
        const rootResources = resources?.[root.id] ? resources[root.id] : void 0;
        const nextRoot = {
          ...root,
          subFoldersCount: 0,
          integration: null,
          folder: {
            ...root.folder,
            sharedWith: rootSharedWith,
            resources: rootResources
          }
        };
        if (root.type === "INTEGRATION") {
          const integration = integrations.find((integration2) => integration2.mirrorRegistries.find((registry) => registry.rootId === root.id));
          return {
            ...nextRoot,
            integration
          };
        }
        return nextRoot;
      });
      const result = {
        count: response.count,
        roots: mappedRoots
      };
      return result;
    },
    // initialData,
    initialPageParam,
    getNextPageParam,
    select: (data) => {
      if (!data) {
        return data;
      }
      const roots = data.pages.map((page) => page.roots.map((root) => {
        if (root.integration) {
          return {
            ...root,
            integration: {
              ...root.integration,
              viewConfig: getIntegrationViewConfig(root.integration)
            }
          };
        }
        return root;
      })).flat();
      return {
        ...data,
        count: data.pages[0]?.count,
        roots
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 1e3 * (isInExtensionPlatform() ? 45 : 10),
    refetchInterval: 1e3 * 60,
    ...queryConfig,
    enabled: isQueryEnabled([
      !!user,
      queryConfig?.enabled ?? true
    ])
  });
  const { data: countPerSubfolder } = useQueryDirectSubfolderCountPerResourceId({
    folderIds: query.data?.roots.map((root) => root.folder.id) || []
  });
  const rootsWithUpdatedSubfolderCount = React__default.useMemo(() => {
    if (!query.data?.roots) {
      return void 0;
    }
    return query.data.roots.map((root) => {
      return {
        ...root,
        subFoldersCount: countPerSubfolder?.[root.folder.id]?.folder || 0
      };
    });
  }, [
    query.data?.roots,
    countPerSubfolder
  ]);
  return {
    ...query,
    roots: rootsWithUpdatedSubfolderCount
  };
};

export { useQueryResourceRootList };
//# sourceMappingURL=useQueryResourceRootList.js.map
